import React, { useEffect, useState } from 'react';
import {
    Box, Card, CardContent, CardHeader,
    Typography, Divider, TextField,
    IconButton, Chip,
    Button, MenuItem
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import {
    collection, getDocs, addDoc,
    updateDoc, deleteDoc, doc
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../firebase";
import Swal from "sweetalert2";

const colors = {
    darkGreen: '#06362D',
    calPolyGreen: '#155332',
    vanilla: '#DEE2B1',
    gold: '#FFD500',
    goldenrod: '#D1A505'
};

function Rules() {
    const auth = getAuth();

    const [rules, setRules] = useState([]);
    const [form, setForm] = useState({
        title: "",
        content: "",
        status: "active"
    });
    const [editId, setEditId] = useState(null);

    const [userData, setUserData] = useState(null);
    const [propertyId, setPropertyId] = useState(null);

    /* ================= AUTH ================= */
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (user) {
                setUserData(user);

                const snap = await getDocs(collection(db, "authroles"));
                snap.forEach(d => {
                    if (d.id === user.uid) {
                        setPropertyId(d.data().propertyId);
                    }
                });
            }
        });
        return () => unsub();
    }, []);

    /* ================= FETCH ================= */
    const fetchRules = async () => {
        if (!userData || !propertyId) return;

        const snap = await getDocs(collection(db, "property_rules"));
        const list = snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(
                r => r.userId === userData.uid && r.propertyId === propertyId
            );

        setRules(list);
    };

    useEffect(() => {
        fetchRules();
    }, [userData, propertyId]);

    /* ================= ADD / UPDATE ================= */
    const saveRule = async () => {
        if (!form.title || !form.content) return;

        if (editId) {
            await updateDoc(doc(db, "property_rules", editId), form);
            Swal.fire("Updated", "Rule updated successfully", "success");
        } else {
            await addDoc(collection(db, "property_rules"), {
                ...form,
                userId: userData.uid,
                propertyId,
                createdAt: new Date()
            });
            Swal.fire("Added", "Rule added successfully", "success");
        }

        setForm({ title: "", content: "", status: "active" });
        setEditId(null);
        fetchRules();
    };

    /* ================= DELETE ================= */
    const deleteRule = async (id) => {
        await deleteDoc(doc(db, "property_rules", id));
        Swal.fire("Deleted", "Rule removed", "success");
        fetchRules();
    };

    /* ================= EDIT ================= */
    const editRule = (rule) => {
        setForm({
            title: rule.title,
            content: rule.content,
            status: rule.status
        });
        setEditId(rule.id);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }}>
            <Box component="main" sx={{ flexGrow: 1, maxWidth: "1400px", margin: "0 auto" }}>
                <Typography variant="h5" fontWeight={700} mb={3} sx={{ color: colors.vanilla }}>
                    📝 Rules & Regulations
                </Typography>

                {/* ================= FORM ================= */}
                <Card sx={{ mb: 4, borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
                    <CardHeader title={editId ? "Edit Rule" : "Add New Rule"} sx={{ '& .MuiCardHeader-title': { color: colors.vanilla, fontWeight: 700 } }} />
                    <Divider sx={{ borderColor: colors.goldenrod, opacity: 0.4 }} />
                    <CardContent sx={{ p: 3 }}>
                        <div className="container-fluid p-0">
                            <div className="row">
                                <div className="col-sm-6 mb-3">
                                    <TextField
                                        label="Rule Title"
                                        fullWidth
                                        size="small"
                                        value={form.title}
                                        onChange={(e) =>
                                            setForm({ ...form, title: e.target.value })
                                        }
                                        InputLabelProps={{ style: { color: colors.vanilla } }}
                                        InputProps={{ style: { color: colors.vanilla } }}
                                        sx={{ 
                                            "& .MuiOutlinedInput-root": { 
                                                borderRadius: "8px",
                                                backgroundColor: colors.darkGreen,
                                                "& fieldset": { borderColor: colors.goldenrod },
                                                "&:hover fieldset": { borderColor: colors.gold },
                                                "&.Mui-focused fieldset": { borderColor: colors.gold }
                                            } 
                                        }}
                                    />
                                </div>

                                <div className="col-sm-6 mb-3">
                                    <TextField
                                        select
                                        label="Status"
                                        size="small"
                                        fullWidth
                                        value={form.status}
                                        onChange={(e) =>
                                            setForm({ ...form, status: e.target.value })
                                        }
                                        InputLabelProps={{ style: { color: colors.vanilla } }}
                                        sx={{ 
                                            color: colors.vanilla,
                                            backgroundColor: colors.darkGreen,
                                            borderRadius: "8px",
                                            "& .MuiOutlinedInput-root": { 
                                                borderRadius: "8px",
                                                "& fieldset": { borderColor: colors.goldenrod },
                                                "&:hover fieldset": { borderColor: colors.gold },
                                                "&.Mui-focused fieldset": { borderColor: colors.gold }
                                            },
                                            '& .MuiSelect-select': { color: colors.vanilla },
                                            '& .MuiSvgIcon-root': { color: colors.vanilla }
                                        }}
                                    >
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="inactive">Inactive</MenuItem>
                                    </TextField>
                                </div>

                                <div className="col-sm-12 mb-3">
                                    <TextField
                                        label="Rule Description"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        value={form.content}
                                        onChange={(e) =>
                                            setForm({ ...form, content: e.target.value })
                                        }
                                        InputLabelProps={{ style: { color: colors.vanilla } }}
                                        InputProps={{ style: { color: colors.vanilla } }}
                                        sx={{ 
                                            "& .MuiOutlinedInput-root": { 
                                                borderRadius: "8px",
                                                backgroundColor: colors.darkGreen,
                                                "& fieldset": { borderColor: colors.goldenrod },
                                                "&:hover fieldset": { borderColor: colors.gold },
                                                "&.Mui-focused fieldset": { borderColor: colors.gold }
                                            } 
                                        }}
                                    />
                                </div>

                                <div className="col-sm-12">
                                    <Button
                                        variant="contained"
                                        startIcon={<Add />}
                                        onClick={saveRule}
                                        sx={{ 
                                            bgcolor: colors.goldenrod, 
                                            color: colors.darkGreen, 
                                            fontWeight: 700, 
                                            textTransform: "none", 
                                            borderRadius: "8px",
                                            '&:hover': { bgcolor: colors.gold } 
                                        }}
                                    >
                                        {editId ? "Update Rule" : "Add Rule"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* ================= RULE LIST (TABLE VIEW) ================= */}
                <Card sx={{ borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
                    <CardHeader title="Rules List" sx={{ '& .MuiCardHeader-title': { color: colors.vanilla, fontWeight: 700 } }} />
                    <Divider sx={{ borderColor: colors.goldenrod, opacity: 0.4 }} />

                    <CardContent sx={{ p: 3 }}>
                        {rules.length === 0 ? (
                            <Typography sx={{ color: colors.vanilla, opacity: 0.7 }}>
                                No rules added yet.
                            </Typography>
                        ) : (
                            <Box sx={{ overflowX: "auto" }}>
                                <table
                                    style={{
                                        width: "100%",
                                        borderCollapse: "collapse"
                                    }}
                                >
                                    <thead>
                                        <tr style={{ background: colors.darkGreen }}>
                                            <th style={{ ...thStyle, color: colors.vanilla }}>Title</th>
                                            <th style={{ ...thStyle, color: colors.vanilla }}>Content</th>
                                            <th style={{ ...thStyle, color: colors.vanilla }}>Status</th>
                                            <th style={{ ...thStyle, color: colors.vanilla, textAlign: "center" }}>Actions</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {rules.map((rule) => (
                                            <tr key={rule.id} style={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                                                <td style={tdStyle}>
                                                    <Typography fontWeight={600} sx={{ color: colors.gold }}>
                                                        {rule.title}
                                                    </Typography>
                                                </td>

                                                <td style={tdStyle}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: colors.vanilla,
                                                            maxWidth: 400,
                                                            whiteSpace: "normal",
                                                            wordBreak: "break-word",
                                                            opacity: 0.9
                                                        }}
                                                    >
                                                        {rule.content}
                                                    </Typography>
                                                </td>

                                                <td style={tdStyle}>
                                                    <Chip
                                                        label={rule.status.toUpperCase()}
                                                        color={rule.status === "active" ? "success" : "default"}
                                                        size="small"
                                                    />
                                                </td>

                                                <td style={{ ...tdStyle, textAlign: "center" }}>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: colors.gold }}
                                                        onClick={() => editRule(rule)}
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>

                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => deleteRule(rule.id)}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Box>
                        )}
                    </CardContent>
                </Card>

            </Box>
        </Box>
    );
}

const thStyle = {
    textAlign: "left",
    padding: "12px",
    fontSize: "14px",
    fontWeight: 650
};

const tdStyle = {
    padding: "12px",
    verticalAlign: "top"
};

export default Rules;